// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ApiService, BaseApiService } from '@fc-core/services/base-api.service';
import { finalize, map, mergeMap, startWith, switchMap } from 'rxjs/operators';
import { from, merge, Observable, of, throwError, timer } from 'rxjs';
import {
  AnyVehicle,
  LocationAwareVehicle,
  Vehicle,
  VehicleTypeDetail,
} from '@fc-core/models';
import { pick } from 'lodash-es';
import { HttpClient, HttpParams } from '@angular/common/http';
import { VehicleSpecSchema } from '@fc-core/models/vehicle/vehicle-schema-model';
import { toCamel } from '@fc-shared/utils/data.utils';
import { Response } from '@fc-core/services/news.service';
import { environment } from '../../../environments/environment';
import { PointGeoJSON } from '@fc-locations/models/geojson';
import { BackendResponse } from '@fc-core/models/response.model';

export const genericRetryStrategy =
  ({ maxRetryAttempts = 3, scalingDuration = 1000 }) =>
  (attempts: Observable<any>) => {
    return attempts.pipe(
      mergeMap((error, i) => {
        const retryAttempt = i + 1;
        if (retryAttempt > maxRetryAttempts) return throwError(error);
        console.log(
          `Attempt ${retryAttempt}: retrying in ${
            retryAttempt * scalingDuration
          }ms`,
        );
        return timer(retryAttempt * scalingDuration);
      }),
      finalize(() => console.log('We are done!')),
    );
  };

function isLocationAwareVehicle(obj: any): obj is LocationAwareVehicle {
  return 'lastReading' in obj && 'lastState' in obj;
}

@Injectable({ providedIn: 'root' })
@ApiService({ baseName: 'vehicles' })
export class VehicleApiService extends BaseApiService {
  constructor(private http: HttpClient) {
    super();
  }

  static toRest(vehicle: AnyVehicle) {
    const output = pick(vehicle, [
      'manufacturer',
      'model',
      'year',
      'identifier',
      'visibility',
      'hardware',
      'harness',
      'groups',
      'ifta',
      'photo',
      'realOdometer',
      'realEngineHours',
      'driver',
      'type',
      'camera',
    ]);

    if (!output.photo) {
      delete output.photo;
    }

    return output;
  }

  public static fromJSON<T extends Vehicle>(vehicle: any): T {
    const obj: any = { ...vehicle };

    if (isLocationAwareVehicle(vehicle)) {
      if (
        vehicle.lastReading &&
        vehicle.lastReading.point &&
        vehicle.lastReading.point.coordinates &&
        vehicle.lastReading.point.coordinates.length
      ) {
        obj.lastReading = {
          ...vehicle.lastReading,
          datetime: new Date(vehicle.lastReading.datetime),
          speed: Math.round(vehicle.lastReading.speed),
          point: {
            lat: vehicle.lastReading.point.coordinates[1],
            lng: vehicle.lastReading.point.coordinates[0],
          },
        };
      }

      if (vehicle.lastState === null) {
        // todo sc-20819
        obj.lastState = {
          type: 'stopped',
          duration: {
            lower: new Date().toISOString(),
            upper: new Date().toISOString(),
          },
        };
      }
    }

    return obj;
  }

  delete(id: number): Observable<any> {
    return this.http.delete(this.getUrl(id));
  }

  makeParams(paramsArray: string[]): HttpParams {
    if (!paramsArray && !paramsArray?.length) return null;
    let params: HttpParams = new HttpParams();
    params = params.set('fields', paramsArray.join());
    return params;
  }

  loadVehiclesPromise(
    url: string,
    params: HttpParams,
    itemsList: AnyVehicle[],
  ): Promise<AnyVehicle[]> {
    return this.http
      .get<BackendResponse<Vehicle[]>>(url, { params })
      .toPromise()
      .then((resp: BackendResponse<Vehicle[]>) => {
        itemsList = [
          ...itemsList,
          ...resp.results.map((vehicle) => VehicleApiService.fromJSON(vehicle)),
        ];
        if (!!resp.next) {
          return this.loadVehiclesPromise(resp.next, params, itemsList);
        }
        return itemsList;
      });
  }

  // todo we can replace it with store data
  loadAllVehicles(paramsItem?: string[]): Observable<AnyVehicle[]> {
    return from(
      this.loadVehiclesPromise(this.getUrl(), this.makeParams(paramsItem), []),
    );
  }

  loadVehicle(vehicleId: number, options?: string[]): Observable<Vehicle> {
    return this.http
      .get(this.getUrl(vehicleId), { params: this.makeParams(options) })
      .pipe(map((resp: any) => VehicleApiService.fromJSON(resp)));
  }

  loadAllVehicleAsync<T extends Vehicle>(
    params?: string[],
  ): Observable<{
    vehicleList: T[];
    count: number;
    totalLoaded: number;
    end: boolean;
  }> {
    let totalLoaded = 0;
    return this.http
      .get<Response>(this.getUrl() + '?limit=10', {
        params: this.makeParams(params),
      })
      .pipe(
        switchMap((data: Response) => {
          const requests: Observable<Response>[] = [];
          if (data.count > 10) {
            for (let i = 10; i <= data.count; i = i + 10) {
              requests.push(this.makeRequests(i, params));
            }
            return merge(...requests).pipe(
              startWith(data),
              map((resp: Response) => {
                totalLoaded += resp.results.length;
                return {
                  vehicleList: resp.results.map((vehicle: any) =>
                    VehicleApiService.fromJSON(vehicle),
                  ),
                  count: data.count,
                  totalLoaded,
                  end: totalLoaded === data.count,
                };
              }),
            );
          }
          return of({
            vehicleList: data.results.map((vehicle: any) =>
              VehicleApiService.fromJSON(vehicle),
            ),
            count: data.count,
            totalLoaded,
            end: true,
          });
        }),
      );
  }

  makeRequests(count: number, params: string[]): Observable<any> {
    return this.http.get<Response>(
      this.getUrl() + `?limit=10&offset=${count}`,
      {
        params: this.makeParams(params),
      },
    );
  }

  create<T extends Vehicle>(vehicle: T): Observable<T> {
    const restVehicle = VehicleApiService.toRest(vehicle);

    return this.http.post(this.getUrl(), restVehicle).pipe(
      map((res) => {
        return VehicleApiService.fromJSON<T>(res);
      }),
    );
  }

  patchVehicle<T extends Vehicle>(vehicle: T, fields: string[]): Observable<T> {
    return this.http.patch(this.getUrl(vehicle.id), pick(vehicle, fields)).pipe(
      map((res) => VehicleApiService.fromJSON<T>(res)),
      switchMap((response) => {
        if (fields.includes('vin') && vehicle.vin) {
          return this.updateVehicleVinNumber(vehicle.id, vehicle.vin).pipe(
            map((vin) => ({ ...response, vin })),
          );
        } else {
          return of(response);
        }
      }),
    );
  }

  updateVehicleVinNumber(vehicleId: number, vin: string): Observable<string> {
    return this.http
      .put<{
        vin: string;
      }>(`${environment.apiUrl}api/vehicles/${vehicleId}/vin/`, { vin })
      .pipe(map((res) => res.vin));
  }

  addSpecToVehicle(
    specSchema: { specifications: any },
    vehicle: Vehicle,
    specName: string,
  ): Observable<Vehicle> {
    const payload = { specifications: { ...vehicle.specifications } };
    payload.specifications[specName] = specSchema.specifications[specName];
    return this.http.patch<Vehicle>(
      `${environment.apiUrl}api/vehicles/${vehicle.id}/`,
      payload,
    );
  }

  deleteSpecFromVehicle(
    vehicle: Vehicle,
    specName: string,
  ): Observable<Vehicle> {
    const payload = { specifications: { ...vehicle.specifications } };
    delete payload.specifications[specName];
    return this.http.patch<Vehicle>(
      `${environment.apiUrl}api/vehicles/${vehicle.id}/`,
      payload,
    );
  }

  getSpecSchemaForVehicle(
    vehicleType: number,
    schemaType: string,
  ): Observable<VehicleSpecSchema> {
    return this.http
      .get<VehicleTypeDetail>(
        `${environment.apiUrl}api/vehicle-types/${vehicleType}/`,
      )
      .pipe(
        map((data: VehicleTypeDetail) => {
          const selectedSchema = data.specifications.filter(
            (spec) => spec.slug === schemaType,
          )[0];
          if (selectedSchema.schema) {
            return {
              ...selectedSchema.schema,
              required: selectedSchema.schema.required?.map(
                (required: string) => toCamel(required),
              ),
            };
          }
        }),
      );
  }

  setVehicleLocation(
    vehicleId: number,
    point: PointGeoJSON,
  ): Observable<{ id: number; point: PointGeoJSON }> {
    return this.http.post<{ id: number; point: PointGeoJSON }>(
      `${environment.apiUrl}api/vehicles/${vehicleId}/location/`,
      { point },
    );
  }
}
